import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ItemSearch, Modal } from '@/lib/components';
import { Button } from '@/lib/components/Button/Button';
import { FloatModal } from '@/lib/v2/components';
import { Flex } from '@/lib/v2/components/Layout/Stack';

import { IContactActivityGroups, IGroup } from '@/src/ContactsModule/interfaces/Interfaces';

import { useGroup } from '@/modules/ContactsModule/hooks/useGroup';

const ButtonsSection = ({
  groups,
  setShowGroupsModal,
  setSelectedGroups,
  setUnselectedGroups,
  selectedGroupsNames,
  setSelectedGroupsNames,
  isUpdateCheckboxModal,
}: {
  groups: IGroup[];
  setShowGroupsModal?: Dispatch<SetStateAction<boolean>>;
  setSelectedGroups?: Dispatch<SetStateAction<number[]>>;
  setUnselectedGroups?: Dispatch<SetStateAction<number[]>>;
  selectedGroupsNames: string[];
  setSelectedGroupsNames: Dispatch<SetStateAction<string[]>>;
  isUpdateCheckboxModal: boolean;
}) => {
  const { t } = useTranslation();
  const [disableButtons, setDisableButtons] = useState(false);

  useEffect(() => {
    !isUpdateCheckboxModal && setDisableButtons(selectedGroupsNames.length === 0);
  }, [selectedGroupsNames]);

  const cleanInputs = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const checkbox: any = document.getElementsByClassName('_modal_checkbox_'); // inputs in <ItemSearch/>
    if (checkbox) {
      for (let i = 0; i < checkbox.length; i++) {
        checkbox[i].checked = false;
      }
    }
  };

  return (
    <Flex withGap alignment="end" gapSize="medium">
      <Button
        stroked
        disabled={disableButtons}
        onAction={() => {
          cleanInputs();
          setSelectedGroupsNames([]);
        }}
      >
        <div className="text-12 lg:text-12 xl:text-14 2xl:text-16">
          {t('CONTACT_GROUP_MODAL.clearGroup')}
        </div>
      </Button>
      <Button
        disabled={disableButtons}
        onAction={() => {
          const selectedGroupsIds = groups
            .filter((g) => selectedGroupsNames.includes(g.groupName))
            .map((g) => Number(g.id));

          const unselectedGroupsIds = groups
            .filter((g) => !selectedGroupsNames.includes(g.groupName))
            .map((g) => Number(g.id));

          setSelectedGroups && setSelectedGroups(selectedGroupsIds);
          setUnselectedGroups && setUnselectedGroups(unselectedGroupsIds);
          //closeModal();
          setShowGroupsModal && setShowGroupsModal(false);
        }}
      >
        <div className="text-12 lg:text-12 xl:text-14 2xl:text-16">{t('Save')}</div>
      </Button>
    </Flex>
  );
};

interface IContactsGroupModalProps {
  groups: IGroup[];
  setSelectedGroups?: Dispatch<SetStateAction<number[]>>;
  setUnselectedGroups?: Dispatch<SetStateAction<number[]>>;
  setListSelectedGroupsNames?: Dispatch<SetStateAction<string[]>>;
  setListUnselectedGroupsNames?: Dispatch<SetStateAction<string[]>>;
  setShowGroupsModal?: Dispatch<SetStateAction<boolean>>;
  maxGroupsToAdd?: number;
  showModal?: boolean;
  contactGroups?: IContactActivityGroups[];
  isUpdateCheckboxModal?: boolean;
  onLastRefresh?: () => void;
}

export const ContactsGroupModal = ({
  contactGroups,
  groups,
  setShowGroupsModal,
  setSelectedGroups,
  setUnselectedGroups,
  setListSelectedGroupsNames,
  setListUnselectedGroupsNames,
  maxGroupsToAdd,
  showModal = true,
  isUpdateCheckboxModal = false,
  onLastRefresh,
}: IContactsGroupModalProps) => {
  const { t } = useTranslation();
  const [selectedGroupsNames, setSelectedGroupsNames] = useState<string[]>([]);
  const [unselectedGroupsNames, setUnselectedGroupsNames] = useState<string[]>([]);
  const [isLoadingCreate, setIsLoadingCreate] = useState(false);

  const { createGroup } = useGroup();

  const contactsID = contactGroups && contactGroups.map((e) => e.name);

  useEffect(() => {
    if (setListSelectedGroupsNames && setListUnselectedGroupsNames) {
      setListSelectedGroupsNames(selectedGroupsNames);
      setListUnselectedGroupsNames(unselectedGroupsNames);
    }
  }, [selectedGroupsNames, unselectedGroupsNames]);

  useEffect(() => {
    if (contactsID) {
      setSelectedGroupsNames(contactsID);
      setUnselectedGroupsNames(contactsID);
    }
  }, []);

  const handleOnCreate = useCallback(
    async (value: string) => {
      setIsLoadingCreate(true);
      const groupCreated = await createGroup(value);
      setIsLoadingCreate(false);
      if (groupCreated) onLastRefresh?.();
    },
    [createGroup, onLastRefresh]
  );

  return (
    <FloatModal>
      <Modal show={showModal}>
        <div className="h-full p-8">
          <Flex column withGap gapSize="medium" itemAlignment="stretch">
            <Modal.Title onAction={() => setShowGroupsModal && setShowGroupsModal(false)}>
              <Flex withGap>
                <p>{t('CONTACT_GROUP_MODAL.title')}</p>
                <p>({selectedGroupsNames.length})</p>
              </Flex>
            </Modal.Title>
            <div className="h-[86%]">
              <Modal.Body withBorder>
                <div className="w-[30vw] p-[5%]">
                  <ItemSearch
                    SearchPlaceHolder={t('CONTACT_GROUP_MODAL.inputDescription')}
                    isLoading={isLoadingCreate}
                    itemList={groups.map((g) => g.groupName)}
                    maxItems={maxGroupsToAdd}
                    selectedItems={selectedGroupsNames}
                    setSelectedItems={setSelectedGroupsNames}
                    setUnselectedItems={setUnselectedGroupsNames}
                    subTitle={t('CONTACT_GROUP_MODAL.createGroup')}
                    title={t('CONTACT_GROUP_MODAL.chooseGroup')}
                    onCreate={handleOnCreate}
                  />
                </div>
              </Modal.Body>
            </div>
            <Modal.Footer>
              <ButtonsSection
                groups={groups}
                isUpdateCheckboxModal={isUpdateCheckboxModal}
                selectedGroupsNames={selectedGroupsNames}
                setSelectedGroups={setSelectedGroups}
                setSelectedGroupsNames={setSelectedGroupsNames}
                setShowGroupsModal={setShowGroupsModal}
                setUnselectedGroups={setUnselectedGroups}
              />
            </Modal.Footer>
          </Flex>
        </div>
      </Modal>
    </FloatModal>
  );
};
